import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, Zap } from 'lucide-react';
import { toast } from 'react-toastify';
import SettingsSlideModal from '../Modal/SettingsSlideModal';

interface Template {
  id: string;
  name?: string;
  element_name?: string;
  status: string;
  category: string;
  language: string;
  parameters?: any[];
  components?: any[];
  content?: string;
  body?: string;
  headerType?: string;
  parameter_format?: string;
}

interface TriggerRule {
  id?: string;
  name?: string;
  trigger_name?: string;
  triggerType?: string;
  trigger_event?: string;
  templateId?: string;
  template_id?: string;
  trigger_conditions?: any;
  parameter_mapping?: Record<string, string>;
  parameterMapping?: Record<string, string>;
  channel?: string;
  is_active?: boolean;
  isActive?: boolean;
  priority?: number;
  toStatus?: string;
  fromStatus?: string;
}

interface AutomationRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  trigger: TriggerRule | null;
  templates: Template[];
  crmColumns: any[];
  statusOptions: any[];
  onSave: (triggerData: any) => Promise<void>;
  provider: string; // 'wati' | 'aisensy' | 'brevo'
}

const AutomationRuleModal: React.FC<AutomationRuleModalProps> = ({
  isOpen,
  onClose,
  trigger,
  templates,
  crmColumns,
  statusOptions,
  onSave,
  provider
}) => {
  const [formData, setFormData] = useState({
    trigger_name: '',
    trigger_event: 'new_lead' as 'new_lead' | 'status_change',
    template_id: '',
    trigger_conditions: {
      recipient_type: 'internal' as 'internal' | 'customer',
      campaign_name: ''
    } as any,
    parameter_mapping: {} as Record<string, string>,
    media_url: '',
    channel: 'whatsapp',
    is_active: true,
    priority: 1
  });

  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (trigger) {
      const triggerEvent = trigger.triggerType || trigger.trigger_event || 'new_lead';
      const templateId = trigger.templateId || trigger.template_id;
      setFormData({
        trigger_name: trigger.name || trigger.trigger_name || '',
        trigger_event: (triggerEvent === 'new_lead' || triggerEvent === 'status_change') ? triggerEvent : 'new_lead',
        template_id: templateId ? templateId.toString() : '',
        trigger_conditions: {
          ...trigger.trigger_conditions,
          recipient_type: trigger.trigger_conditions?.recipient_type || 'internal',
          campaign_name: trigger.trigger_conditions?.campaign_name || '',
          target_status: (() => {
            if (trigger.toStatus) {
              const statusByOptId = (statusOptions || []).find(opt => opt.opt_id.toString() === trigger.toStatus);
              if (statusByOptId) {
                return trigger.toStatus;
              }
              const statusByOptValue = (statusOptions || []).find(opt => opt.opt_value === trigger.toStatus);
              return statusByOptValue ? statusByOptValue.opt_id.toString() : trigger.toStatus;
            }
            return trigger.trigger_conditions?.target_status || '';
          })()
        },
        parameter_mapping: trigger.parameterMapping || trigger.parameter_mapping || {},
        media_url: trigger.parameterMapping?.media_url || trigger.parameter_mapping?.media_url || '',
        channel: trigger.channel || 'whatsapp',
        is_active: trigger.isActive !== undefined ? trigger.isActive : (trigger.is_active !== undefined ? trigger.is_active : true),
        priority: trigger.priority || 1
      });
    } else {
      setFormData({
        trigger_name: '',
        trigger_event: 'new_lead',
        template_id: '',
        trigger_conditions: {},
        parameter_mapping: {},
        media_url: '',
        channel: 'whatsapp',
        is_active: true,
        priority: 1
      });
    }
  }, [trigger, statusOptions]);

  useEffect(() => {
    const template = (templates || []).find(t => t.id.toString() === formData.template_id);
    setSelectedTemplate(template || null);

    if (template) {
      const newMapping: Record<string, string> = {};
      if (template.parameters && Array.isArray(template.parameters)) {
        template.parameters.forEach((_param, index) => {
          newMapping[`${index + 1}`] = formData.parameter_mapping[`${index + 1}`] || '';
        });
      }
      setFormData(prev => ({ ...prev, parameter_mapping: newMapping }));
    }
  }, [formData.template_id, templates]);

  const handleSave = async () => {
    if (!formData.trigger_name || !formData.template_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSaving(true);
    try {
      const parameterMapping = { ...formData.parameter_mapping };
      if (formData.media_url) {
        parameterMapping.media_url = formData.media_url;
      }

      const { media_url, ...triggerDataWithoutMediaUrl } = formData;
      const triggerData = {
        ...triggerDataWithoutMediaUrl,
        template_id: parseInt(formData.template_id),
        parameter_mapping: parameterMapping,
        ...(trigger && { id: trigger.id })
      };

      if (formData.trigger_event === 'status_change') {
        if (!formData.trigger_conditions.target_status) {
          toast.error('Please select the target status for status change trigger');
          setIsSaving(false);
          return;
        }
      }

      await onSave(triggerData);
    } catch (error) {
      console.error('Error saving trigger:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SettingsSlideModal
      isOpen={isOpen}
      onClose={onClose}
      title={trigger ? 'Edit Automation Rule' : 'Create Automation Rule'}
      subtitle={trigger ? 'Modify automation settings and parameter mapping' : `Set up automated ${provider.toUpperCase()} messages for CRM events`}
      icon={<Zap className="w-6 h-6" />}
    >
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Rule Name *
              </label>
              <input
                type="text"
                value={formData.trigger_name}
                onChange={(e) => setFormData({ ...formData, trigger_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                placeholder="e.g., Welcome New Leads"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Trigger Event *
              </label>
              <select
                value={formData.trigger_event}
                onChange={(e) => setFormData({
                  ...formData,
                  trigger_event: e.target.value as 'new_lead' | 'status_change',
                  trigger_conditions: {}
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
              >
                <option value="new_lead">New Lead</option>
                <option value="status_change">Status Change</option>
              </select>
            </div>
          </div>

          {/* Recipient Type */}
          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Message Recipient
            </h4>
            <div className="space-y-3">
              <label className="flex items-start p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-white dark:hover:bg-gray-700 transition-colors">
                <input
                  type="radio"
                  name="recipient_type"
                  value="internal"
                  checked={formData.trigger_conditions.recipient_type === 'internal'}
                  onChange={() => setFormData({
                    ...formData,
                    trigger_conditions: { ...formData.trigger_conditions, recipient_type: 'internal' }
                  })}
                  className="mt-0.5 text-brandColor focus:ring-brandColor"
                />
                <div className="ml-3">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">Internal (Staff Notifications)</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Send to assigned user + notification role users</div>
                </div>
              </label>

              <label className="flex items-start p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-white dark:hover:bg-gray-700 transition-colors">
                <input
                  type="radio"
                  name="recipient_type"
                  value="customer"
                  checked={formData.trigger_conditions.recipient_type === 'customer'}
                  onChange={() => setFormData({
                    ...formData,
                    trigger_conditions: { ...formData.trigger_conditions, recipient_type: 'customer' }
                  })}
                  className="mt-0.5 text-brandColor focus:ring-brandColor"
                />
                <div className="ml-3">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">Customer (Lead/Contact)</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Send to the lead's phone number</div>
                </div>
              </label>
            </div>
          </div>

          {/* Campaign Name (for AiSensy tracking) */}
          {provider === 'aisensy' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Campaign Name (Optional)
              </label>
              <input
                type="text"
                value={formData.trigger_conditions.campaign_name || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  trigger_conditions: { ...formData.trigger_conditions, campaign_name: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                placeholder="Leave empty to use template name"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Custom campaign name for AiSensy tracking. If empty, template name will be used.
              </p>
            </div>
          )}

          {/* Status Change Conditions */}
          {formData.trigger_event === 'status_change' && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-3">
                Status Change Conditions
              </h4>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Target Status (trigger when lead changes to this status)
                  </label>
                  <select
                    value={formData.trigger_conditions.target_status || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      trigger_conditions: { ...formData.trigger_conditions, target_status: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select target status</option>
                    {(statusOptions || []).map((option) => (
                      <option key={option.opt_id} value={option.opt_id}>
                        {option.opt_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Template Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {provider.toUpperCase()} Template *
            </label>
            <select
              value={formData.template_id}
              onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
            >
              <option value="">Select a template</option>
              {(templates || []).filter(t => t.status === 'APPROVED').map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name || template.element_name} ({template.category})
                </option>
              ))}
            </select>
          </div>

          {/* Template Preview */}
          {selectedTemplate && (
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Template Preview</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                {selectedTemplate.content || selectedTemplate.body}
              </p>
            </div>
          )}

          {/* Parameter Mapping */}
          {selectedTemplate && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                Parameter Mapping
              </h4>
              {selectedTemplate.parameters && selectedTemplate.parameters.length > 0 ? (
                <div className="space-y-3">
                  {selectedTemplate.parameters
                    .filter((param: any) => param.section !== 'button')
                    .map((param: any, index: number) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-32">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {param.section ? (
                            <span className="flex items-center gap-1">
                              <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-brandColor dark:text-blue-400 rounded">
                                {param.section.toUpperCase()}
                              </span>
                              {`{{${param.index || index + 1}}}`}
                            </span>
                          ) : (
                            `{{${index + 1}}}`
                          )}
                        </span>
                      </div>
                      <div className="flex-1">
                        <select
                          value={formData.parameter_mapping[`${index + 1}`] || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            parameter_mapping: {
                              ...formData.parameter_mapping,
                              [`${index + 1}`]: e.target.value
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                        >
                          <option value="">Select field to map</option>
                          {(crmColumns || []).map((column) => (
                            <option key={column.col_id} value={column.col_name}>
                              {column.col_label} ({column.col_name})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    This template doesn't have any parameters to map.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Media URL */}
          {selectedTemplate && (selectedTemplate.headerType === 'IMAGE' || selectedTemplate.headerType === 'VIDEO' || selectedTemplate.headerType === 'DOCUMENT') && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                Media Attachment
              </h4>
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-3">
                      This template requires a {selectedTemplate.headerType.toLowerCase()} attachment.
                    </p>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Media URL ({selectedTemplate.headerType.toLowerCase()})
                      </label>
                      <input
                        type="url"
                        value={formData.media_url}
                        onChange={(e) => setFormData({ ...formData, media_url: e.target.value })}
                        placeholder={`https://example.com/media.${selectedTemplate.headerType === 'IMAGE' ? 'jpg' : selectedTemplate.headerType === 'VIDEO' ? 'mp4' : 'pdf'}`}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Additional Settings */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Priority
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 1 })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Channel
              </label>
              <select
                value={formData.channel}
                onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                disabled
              >
                <option value="whatsapp">WhatsApp</option>
              </select>
            </div>

            <div className="flex items-center justify-center">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-brandColor rounded focus:ring-brandColor"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Active
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-900">
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brandColor disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            {isSaving ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{isSaving ? 'Saving...' : (trigger ? 'Update Rule' : 'Create Rule')}</span>
          </button>
        </div>
      </div>
    </SettingsSlideModal>
  );
};

export default AutomationRuleModal;